import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    Typography,
    TextField,
    Checkbox,
    FormControlLabel,
    Stack,
    styled,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { isValidEmail, isValidPhoneNumber } from "utilities";

import Page from "components/ui/Page/Page";
import { FormFieldContainer } from "components/ui/FormField";
import {
    FormContainer,
    inputStyles,
} from "components/ui/FormField/FormFieldContainer";
import { ReactComponent as GreenTick } from "assets/img/GreenTick.svg";
import { ReactComponent as GreyDash } from "assets/img/dashIcon.svg";
import AccountMigrateModal from "./partials/AccountMigrateModal";
import Login from "./partials/Login";
import LoginWarningModal from "./partials/LoginWarningModal";

const CriteriaCheckContainer = styled(Stack)(({ theme, isValid }) => ({
    pl: 2,
    color: "#475569",
    justifyContent: "center",
    textAlign: "left",
    listStyle: "none",
    gap: 1,
    "& svg": {
        position: "relative",
        top: "4px",
        marginRight: "8px",
    },
}));

const CreateAccount = ({
    formData,
    onChange,
    onSubmit,
    onMigrateUser,
    isSubmitting,
}) => {
    const [loginScreen, setLoginScreen] = useState(false);
    const [showLoginWarningModal, setShowLoginWarningModal] = useState(false);
    const [showMigrationModal, setShowMigrationModal] = useState(false);
    const [error, setError] = useState({
        email: "",
        phone: "",
        migrate: "",
    });
    const [loginData, setLoginData] = useState(null);

    const passwordCriteria = [
        {
            text: "8-20 Characters (No Spaces)",
            check:
                formData.password.length >= 8 &&
                formData.password.length <= 20 &&
                !formData.password.includes(" "),
        },
        {
            text: "At Least One Upper Case Letter",
            check: /[A-Z]/.test(formData.password),
        },
        {
            text: "At Least One Lower Case Letter",
            check: /[a-z]/.test(formData.password),
        },
        { text: "At Least One Number", check: /[0-9]/.test(formData.password) },
    ];

    const passwordConfirmed =
        formData.password && formData.password === formData.confirmPassword;

    const emailConfirmed =
        formData.email && formData.email === formData.confirmEmail;

    const formValid = () => {
        const emailValid = isValidEmail(formData.email);
        const phoneValid = isValidPhoneNumber(formData.phone);
        const emailPasswordValid =
            emailValid &&
            phoneValid &&
            emailConfirmed &&
            passwordConfirmed &&
            passwordCriteria.every((item) => item.check);
        return emailPasswordValid && formData.terms;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === "email") {
            if (!isValidEmail(value)) {
                setError((prev) => ({
                    ...prev,
                    email: "Please enter a valid email address",
                }));
            } else {
                setError((prev) => ({ ...prev, email: "" }));
            }
        }
        if (name === "phone") {
            if (!isValidPhoneNumber(value)) {
                setError((prev) => ({
                    ...prev,
                    phone: "Please enter a valid phone number",
                }));
            } else {
                setError((prev) => ({ ...prev, phone: "" }));
            }
        }
        onChange("createAccount", name, type === "checkbox" ? checked : value);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit().catch((errors) => {
            if (errors?.[0]?.code === "E#2WKD0D") {
                setShowLoginWarningModal(true);
            } else if (errors?.[0]?.code === "E#2WKCZW") {
                setError((prev) => ({
                    ...prev,
                    email: "This email is already associated with an account.",
                }));
            }
        });
    };

    const gotoLoginScreen = () => {
        setShowLoginWarningModal(false);
        setLoginScreen(true);
    };

    const onLoginSuccess = (loginData) => {
        setLoginScreen(false);
        setShowMigrationModal(true);
        setLoginData(loginData);
    };

    const handleMigrateUser = async () => {
        onMigrateUser(loginData).catch(() => {
            setError((prev) => ({
                ...prev,
                migrate: "Migration failed. Please try again.",
            }));
            window.scrollTo(0, document.body.scrollHeight); // Scroll to bottom to show error message
        }).finally(() => {
            setShowMigrationModal(false);
            setError((prev) => ({ ...prev, migrate: "" }));
        });
    }

    if (loginScreen) {
        return (
            <Login
                email={formData.email}
                onLoginSuccess={onLoginSuccess}
                onCancel={() => setLoginScreen(false)}
            />
        );
    }

    return (
        <>
            <Page
                heading="Create Account"
                subHeading="You’ll need to create an account to log into the Kardia app to start your heart health journey."
            >
                <Typography variant="body2" sx={{ color: "#475569", fontSize: "16px" }}>
                    Already have a Kardia account?{" "}
                    <Link onClick={gotoLoginScreen}>Log In</Link>.
                </Typography>
                <FormContainer
                    sx={{
                        width: "65%",
                    }}
                >
                    <FormFieldContainer
                        heading="Email"
                        required
                        subHeading="If you already have a Kardia account, please login above. We recommend using a personal email address."
                        errorMessage={error.email}
                        fields={[
                            <TextField
                                fullWidth
                                name="email"
                                placeholder="Email"
                                value={formData.email}
                                onChange={handleChange}
                                sx={inputStyles(!!error.email)}
                                error={!!error.email}
                            />,
                        ]}
                    />
                    <FormFieldContainer
                        heading="Confirm Email"
                        required
                        fields={[
                            <TextField
                                fullWidth
                                name="confirmEmail"
                                placeholder="Confirm Email"
                                value={formData.confirmEmail}
                                onChange={handleChange}
                                sx={inputStyles()}
                            />,
                        ]}
                    />
                    <CriteriaCheckContainer>
                        <li>
                            {emailConfirmed ? <GreenTick /> : <GreyDash />}
                            Email and Confirm Emails should match.
                        </li>
                    </CriteriaCheckContainer>

                    <FormFieldContainer
                        heading="Phone Number"
                        required
                        errorMessage={error.phone}
                        fields={[
                            <TextField
                                fullWidth
                                name="phone"
                                placeholder="Phone Number"
                                value={formData.phone}
                                onChange={handleChange}
                                sx={inputStyles(!!error.phone)}
                                error={!!error.phone}
                            />,
                        ]}
                    />

                    <FormFieldContainer
                        heading="Password"
                        required
                        fields={[
                            <TextField
                                fullWidth
                                name="password"
                                placeholder="Password"
                                type="password"
                                value={formData.password}
                                onChange={handleChange}
                                inputProps={{
                                    autoComplete: "new-password",
                                    form: {
                                        autocomplete: "off",
                                    },
                                }}
                                sx={inputStyles()}
                            />,
                        ]}
                    />

                    <CriteriaCheckContainer>
                        {passwordCriteria.map((item, index) => (
                            <li key={index}>
                                {item.check ? <GreenTick /> : <GreyDash />} {item.text}
                            </li>
                        ))}
                    </CriteriaCheckContainer>

                    <FormFieldContainer
                        heading="Confirm Password"
                        required
                        fields={[
                            <TextField
                                fullWidth
                                name="confirmPassword"
                                placeholder="Confirm Password"
                                type="password"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                sx={inputStyles()}
                            />,
                        ]}
                    />

                    <CriteriaCheckContainer>
                        <li>
                            {passwordConfirmed ? <GreenTick /> : <GreyDash />} Password and
                            Confirm Password should match.
                        </li>
                    </CriteriaCheckContainer>

                    <FormFieldContainer
                        fields={[
                            <FormControlLabel
                                sx={{
                                    "& .MuiFormControlLabel-asterisk": {
                                        color: "red",
                                    },
                                }}
                                required
                                control={
                                    <Checkbox
                                        name="terms"
                                        checked={formData.terms}
                                        onChange={handleChange}
                                    />
                                }
                                label={
                                    <>
                                        I agree to the{" "}
                                        <Link to="/terms" target="_blank">
                                            Terms of Service
                                        </Link>{" "}
                                        and{" "}
                                        <Link to="/privacy" target="_blank">
                                            Privacy Policy
                                        </Link>
                                    </>
                                }
                            />,
                        ]}
                    />
                    <FormFieldContainer
                        required
                        fields={[
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="consent"
                                        checked={formData.consent}
                                        onChange={handleChange}
                                    />
                                }
                                label="I consent to receive marketing communications from AliveCor about products and services. I can opt out at anytime, as described in the Privacy Policy."
                            />,
                        ]}
                    />

                    <LoadingButton
                        variant="contained"
                        onClick={handleSubmit}
                        sx={{ mt: 3 }}
                        loading={isSubmitting}
                        disabled={!formValid() || !!error.email || !!error.phone}
                    >
                        Submit
                    </LoadingButton>
                </FormContainer>
                {error.migrate && (
                    <Typography sx={{ mt: 2, color: "error.main" }}>
                        {error.migrate}
                    </Typography>
                )}
            </Page>
            <LoginWarningModal
                open={showLoginWarningModal}
                onContinue={gotoLoginScreen}
                onClose={() => setShowLoginWarningModal(false)}
            />
            <AccountMigrateModal
                open={showMigrationModal}
                onContinue={handleMigrateUser}
                onClose={() => setShowMigrationModal(false)}
                isSubmitting={isSubmitting}
            />
        </>
    );
};

export default CreateAccount;
